import { TRAIT_SEEDS } from '../../../db/traits.js';
import { createSeededRng } from '../../../domain/seededRandom.js';
import type { CharacterBasic, CharacterDraft, CharacterNature, LearnedTrait, WorldDraft } from '../../../domain/types.js';
import { blankNature, defaultAppearance } from './defaults.js';
import { randomAppearancePick } from './appearanceOptions.js';

const FIRST_NAMES = ['Mara', 'Jonas', 'Priya', 'Aiko', 'Ellis', 'Wren', 'Lena', 'Iris', 'Felix', 'Zadie'];
const INITIALS = ['A', 'B', 'C', 'K', 'M', 'R', 'S', 'T', 'V', 'W'];

function pick<T>(items: readonly T[], rng: () => number): T {
  return items[Math.floor(rng() * items.length)]!;
}

function seedFor(draft: WorldDraft, suffix: string): string {
  return `${draft.draftId}:${draft.location?.region ?? ''}:${draft.timePeriod?.period ?? ''}:${suffix}`;
}

/** Privacy-safe names — never expands last initial to full surname. */
export function suggestBasicIdentity(draft: WorldDraft, index: number): CharacterBasic {
  const rng = createSeededRng(seedFor(draft, `name:${index}`));
  return {
    firstName: pick(FIRST_NAMES, rng),
    lastInitial: pick(INITIALS, rng),
  };
}

export function suggestDetailedLook(
  draft: WorldDraft,
  species = 'Human',
): NonNullable<CharacterDraft['appearance']> {
  return { ...defaultAppearance(), ...randomAppearancePick(draft, species) };
}

export function suggestNature(draft: WorldDraft, characterId: string): CharacterNature {
  const rng = createSeededRng(seedFor(draft, `nature:${characterId}`));
  const v = (): number => Math.floor(rng() * 11);
  return {
    sociability: v(),
    agreeableness: v(),
    emotionality: v(),
    caution: v(),
    discipline: v(),
    dominance: v(),
    empathy: v(),
    curiosity: v(),
    drive: v(),
    sexual_drive: v(),
    casual_sex_openness: v(),
  };
}

export function suggestTrait(draft: WorldDraft, characterId: string, index: number): LearnedTrait {
  const rng = createSeededRng(seedFor(draft, `trait:${characterId}:${index}`));
  const [traitKey] = pick(TRAIT_SEEDS, rng);
  return {
    traitKey,
    strength: Math.floor(rng() * 11),
    reason: 'Suggested by AI — edit freely.',
  };
}

export function suggestNatureOrDefault(draft: WorldDraft, characterId: string): CharacterNature {
  return suggestNature(draft, characterId);
}

export function balancedLabel(value: number): string {
  if (value <= 4) return 'low';
  if (value >= 7) return 'high';
  return 'balanced';
}

export { blankNature };
